import { Injectable, inject } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, filter, Observable, Subject, timer } from 'rxjs';
import { take } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { environment } from '@env';

interface ISignalRMessage<T = unknown> {
  action: string;
  item?: T;
  model?: string;
}

@Injectable({
  providedIn: 'root',
})
export class SignalRService {
  private readonly auth = inject(AuthService);

  private hubConnection!: signalR.HubConnection;
  private message = new BehaviorSubject<ISignalRMessage | null>(null);
  private _isConnected = new BehaviorSubject(false);

  private _sendSub: Subject<{
    name: string;
    params: (string | number)[];
  }> = new Subject<{
    name: string;
    params: (string | number)[];
  }>();

  constructor() {
    this._sendSub.pipe().subscribe((msg) => {
      timer(5000)
        .pipe(take(1))
        .subscribe(() => {
          if (this._isConnected.value) {
            this._actualInvoke(msg.name, ...msg.params);
          }
        });
    });
  }

  public subscribeByModel$<T = unknown>(model: string): Observable<ISignalRMessage<T>> {
    return this.message.asObservable().pipe(filter((message) => message?.model === model)) as Observable<
      ISignalRMessage<T>
    >;
  }

  public startConnection() {
    this.auth.getUser().subscribe(() => {
      if (this.hubConnection) {
        this.hubConnection.stop().finally(() => {});
      }
      this.hubConnection = new signalR.HubConnectionBuilder()
        .withUrl(environment.wsSignalR, {
          accessTokenFactory: () => this.auth.token as string,
          skipNegotiation: true,
          transport: signalR.HttpTransportType.WebSockets,
        })
        .build();

      this.hubConnection
        .start()
        .then(() => {
          this._isConnected.next(true);
        })
        .catch((err) => console.error('Błąd podczas utworzenia połączenia:', err));

      this.hubConnection.on('message', (data) => {
        this.message.next(data);
      });
    });
  }

  public joinGroup(...params: (string | number)[]) {
    return this._send('JoinGroup', ...params);
  }

  public leaveGroup(...params: (string | number)[]) {
    return this._send('LeaveGroup', ...params);
  }

  private _send(name: string, ...params: (string | number)[]) {
    if (!this._isConnected.value) {
      this._sendSub.next({ name, params });
    } else {
      this._actualInvoke(name, ...params);
    }
  }

  private _actualInvoke(name: string, ...params: (string | number)[]) {
    this.hubConnection.invoke(name, ...params).catch((err) => console.error(err));
  }
}
